import { useState, useEffect, useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  Button,
  Grid,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'src/components/snackbar';
import { _socials, _userCards } from 'src/_mock';
import { SearchNotFound } from 'src/components/search-not-found';
import { paths } from 'src/routes/paths';
import { useAuthContext } from 'src/auth/hooks';
import {
  delete_parent_program,
  delete_program,
  get_plate_members,
  release_program,
} from 'src/auth/context/jwt/program';
import { ActivityCardSkeleton } from './activity-card-skeleton';
import { useLocation, useNavigate } from 'react-router';
import { MemberPlateCard } from './member-plate-card';

// ----------------------------------------------------------------------

export function PlateMembers() {
  const { user } = useAuthContext();
  const location = useLocation();
  const { program_id } = location.state || {};
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const notFound = activities?.length === 0;

  // Get programs with debounced search
  const getPrograms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await get_plate_members({ id: program_id, search: debouncedSearch });
      if (res?.status === 200 || res?.status === 201) {
        setActivities(res?.data?.data);
      }
    } catch (error) {
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, program_id]);

  // Open delete confirmation dialog
  const handleOpenDialog = (activity) => {
    setSelectedActivity(activity);
    setOpenDialog(true);
  };

  // Close delete confirmation dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedActivity(null);
  };

  // Handle deleting activity
  const handleDeleteActivity = async () => {
    if (selectedActivity) {
      try {
        setDeleteLoading(true);

        // TODO: Replace with actual API call
        const res =
          selectedActivity?.status === 'approval_requested'
            ? await delete_parent_program({ id: selectedActivity?.id })
            : await delete_program({ id: selectedActivity?.id });
        toast.success(res?.message);

        getPrograms();
      } catch (error) {
        toast.error(typeof error === 'string' ? error : error?.error);
      } finally {
        handleCloseDialog();
        setDeleteLoading(false);
      }
    }
  };

  // Handle editing activity
  const handleEditActivity = (activity) => {
    // TODO: Navigate to edit page or open edit modal
    navigate(`${paths.dashboard.general.managePlate}`, {
      state: { activity_id: activity?.id, type: 'edit', is_trail: activity?.is_trail },
    });
  };

  const [openRelease, setOpenRelease] = useState(false);
  // Open delete confirmation dialog
  const handleOpenRelease = (activity) => {
    setSelectedActivity(activity);
    setOpenRelease(true);
  };

  // Close delete confirmation dialog
  const handleCloseRelease = () => {
    setOpenRelease(false);
    setSelectedActivity(null);
  };

  // Handle deleting activity
  const handleReleaseActivity = async () => {
    if (selectedActivity) {
      try {
        setDeleteLoading(true);

        // TODO: Replace with actual API call
        const res = await release_program({ id: selectedActivity?.id });
        toast.success(res?.message);
        getPrograms();
      } catch (error) {
        toast.error(typeof error === 'string' ? error : error?.error);
      } finally {
        handleCloseRelease();
        setDeleteLoading(false);
      }
    }
  };

  // Debounce search functionality
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Fetch programs on mount and when debounced search changes
  useEffect(() => {
    getPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Stack
        spacing={2}
        justifyContent="space-between"
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ mb: 2 }}
      >
        <Typography variant="h4">Programs</Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
        {/* <TextField
          value={search}
          onChange={(e) => {
            setSearch(e?.target?.value);
          }}
          placeholder="Search Programs..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: search.length > 0 && (
              <IconButton
                onClick={() => {
                  setSearch('');
                }}
              >
                <Iconify icon="bitcoin-icons:cross-filled" />
              </IconButton>
            ),
          }}
          sx={{ mb: 2, width: '100%' }}
        /> */}
      </Stack>

      {/* Activities Grid */}
      {loading ? (
        <Grid container spacing={3}>
          {Array.from(new Array(4)).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <ActivityCardSkeleton key={index} />
            </Grid>
          ))}
        </Grid>
      ) : activities?.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {activities?.map((activity) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={activity.id}>
                <MemberPlateCard
                  activity={activity}
                  onDelete={() => handleOpenDialog(activity)}
                  onEdit={handleEditActivity}
                  onRefresh={getPrograms}
                  onRelease={() => {
                    handleOpenRelease(activity);
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        notFound &&
        !loading && (
          <SearchNotFound message="No Programs Found" query={debouncedSearch} sx={{ py: 10 }} />
        )
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Request Delete Program</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {selectedActivity?.status === 'approval_requested' ? (
              'Are you sure you want to direct delete this program ?'
            ) : (
              <>
                Are you sure you want to generate request to delete program{' '}
                <strong style={{ textTransform: 'capitalize' }}>{selectedActivity?.title}</strong>?
                This action is <strong>irreversible</strong>.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleteLoading} onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteActivity}
            loading={deleteLoading}
            color="error"
            autoFocus
            disabled={deleteLoading}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
      {/* DELETE RELEASE DIALOG */}
      <Dialog
        open={openRelease}
        onClose={handleCloseRelease}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Release Program</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to release this Program ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={deleteLoading} onClick={handleCloseRelease} color="primary">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleReleaseActivity}
            loading={deleteLoading}
            color="error"
            autoFocus
            disabled={deleteLoading}
          >
            Release
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
